import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardFooter, CardHeader, Col, Row } from 'reactstrap';
import { TranslationKey, useTranslation } from '../translations';
import { FilterOptions } from '.';

import './FilterTable.scss';

export type SelectedOptions<T> = Record<string, FilterOption<T>[]>;

export interface FilterOption<T> {
  name: TranslationKey;
  value: string;
  readonly map?: (data: T) => T;
  readonly filter: (data: T, options: SelectedOptions<T>) => boolean;
}

export interface Filter<T> {
  name: string;
  displayName: TranslationKey;
  options: FilterOption<T>[];
}

interface FilterTableProps<T> {
  readonly data: T[];
  readonly filters: Filter<T>[];
  readonly infoUrl?: string;
  readonly renderer: (data: T) => React.JSX.Element;
  readonly infoRenderer?: (data?: T) => React.JSX.Element;
}

interface Identifiable {
  id: number;
}

interface TextSearchNode {
  [key: string]: TextSearchNode | number | string;
}

const checkFilterText = (data: TextSearchNode | number | string, searchText: string): boolean => {
  if (!data) {
    return false;
  }

  if (searchText.length === 0) {
    return true;
  }

  if (typeof data === 'object') {
    return Object.values(data).some((v) => checkFilterText(v, searchText));
  }

  return data.toString().toLowerCase().includes(searchText.toLowerCase());
};

const filterFunc = <T,>(
  data: T[],
  searchText: string,
  selectedOptions: SelectedOptions<T>,
): T[] => {
  const opts = Object.values(selectedOptions).filter((v) => v.length > 0);

  // Run map-reduce
  const mappedData: T[] = data.map((d) => {
    // Flat map from "Array<Group<Option>>" to just "Array<Option>"
    const options = opts.flat();

    // Find if anyone wants to change the data before being filtered
    return options.reduce<T>((value, option) => {
      return option.map ? option.map(value) : value;
    }, d);
  });

  const filteredData = mappedData.filter((d) => {
    if (opts.length === 0) {
      return true;
    }

    return opts.every((o) => {
      return o.some(({ filter }) => filter(d, selectedOptions));
    });
  });

  return filteredData.filter((fd) => checkFilterText(fd as unknown as TextSearchNode, searchText));
};

export const FilterTable = <T extends Identifiable>({
  data,
  filters,
  renderer,
  infoRenderer,
  infoUrl,
}: FilterTableProps<T>): React.JSX.Element => {
  const [visibleData, setVisibleData] = useState<T[]>([]);
  const [selectedDataId, setSelectedDataId] = useState<number | undefined>(undefined);
  const isFiltered = visibleData.length !== data.length;
  const selectedData = data.find(({ id }) => id === selectedDataId);
  const { ts } = useTranslation();

  const resetSelection = useCallback(() => {
    setSelectedDataId(undefined);
  }, []);

  const updateData = useCallback(
    (searchText: string, selectedOptions: SelectedOptions<T>): void => {
      setVisibleData(filterFunc(data, searchText, selectedOptions));
    },
    [data],
  );

  return (
    <Row className={selectedData && 'hasSelectedRow'}>
      <Col xs={12}>
        <FilterOptions<T> filters={filters} onUpdate={updateData} />
      </Col>
      <Col xs={12}>
        <br />
      </Col>
      <Col className="resultTable" lg={infoRenderer ? 8 : 12} xs={12}>
        <Card className="filter-table">
          <CardHeader>
            {visibleData.length} {ts('common.results')}
            {isFiltered && ` (Filtered from ${data.length})`}
          </CardHeader>
          {visibleData.map((d) => {
            const id = `row${d.id}`;
            if (!infoRenderer) {
              return (
                <Link
                  id={id}
                  key={d.id}
                  style={{ color: 'inherit', textDecoration: 'inherit' }}
                  to={`${infoUrl!}/${d.id}`}
                >
                  {renderer(d)}
                </Link>
              );
            }

            return (
              <div
                id={id}
                key={d.id}
                onClick={() => {
                  setSelectedDataId(d.id);
                }}
              >
                {renderer(d)}
              </div>
            );
          })}
        </Card>
      </Col>
      {infoRenderer && (
        <Col className="resultInfo" lg={4} xs={12}>
          <Card>
            {' '}
            {infoRenderer(selectedData)}
            <CardFooter>
              <Button block color="secondary" onClick={resetSelection} outline type="button">
                {ts('close_info')}
              </Button>
            </CardFooter>
          </Card>
        </Col>
      )}
    </Row>
  );
};
